import React from 'react';
import Header from '@amzn/awsui-components-console/header';
import Table from '@amzn/awsui-components-console/table';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import { ApolloError } from '@apollo/client';
import { Translation } from 'src/generated/graphql';
import { TranslationStatusIndicator } from 'src/common/translationStatus';

interface LocalesTableProps {
  translations: Translation[];
  loading: boolean;
  error?: ApolloError;
}

/**
 * DEMO-ONLY: Hardcoded "last updated" metadata for design mockup parity.
 *
 * The Smithy `Translation` structure (LangHubServiceModel) only has `locale`,
 * `translatedText`, and `status` — there are NO `updatedAt` or `updatedBy`
 * fields on the Translation type today. This is a genuine API gap.
 *
 * This lookup provides plausible demo values keyed by locale code. Locales not
 * present in this map will gracefully render an em dash (—) placeholder.
 *
 * Remove this constant and the corresponding columns when real API fields are
 * added to the Translation structure.
 */
const DEMO_LAST_UPDATED: Record<
  string,
  { lastUpdatedOn: string; lastUpdatedBy: string }
> = {
  'de-DE': { lastUpdatedOn: '02/07/2026', lastUpdatedBy: 'menshara@' },
  'fr-FR': { lastUpdatedOn: '01/07/2026', lastUpdatedBy: 'jdoe@' },
  'ar-AE': { lastUpdatedOn: '28/06/2026', lastUpdatedBy: 'translator1@' },
  'zh-CN': { lastUpdatedOn: '30/06/2026', lastUpdatedBy: 'lzhang@' },
  'ja-JP': { lastUpdatedOn: '29/06/2026', lastUpdatedBy: 'tanaka@' },
  'es-ES': { lastUpdatedOn: '25/06/2026', lastUpdatedBy: 'garcia@' },
  'pt-BR': { lastUpdatedOn: '27/06/2026', lastUpdatedBy: 'silva@' },
  'it-IT': { lastUpdatedOn: '26/06/2026', lastUpdatedBy: 'rossi@' },
};

const LocalesTable: React.FC<LocalesTableProps> = ({
  translations,
  loading,
  error,
}) => {
  if (error) {
    return (
      <Alert type="error" header="Failed to load translations">
        {error.message}
      </Alert>
    );
  }

  return (
    <Table
      header={
        <Header
          counter={
            !loading && translations.length > 0
              ? `(${translations.length})`
              : undefined
          }
        >
          Locales
        </Header>
      }
      loading={loading}
      loadingText="Loading translations…"
      items={translations}
      columnDefinitions={[
        {
          id: 'locale',
          header: 'Locale',
          cell: (item) => (
            <span data-testid="locale-row" data-locale={item.locale}>
              <span data-testid="locale-code">{item.locale}</span>
            </span>
          ),
        },
        {
          id: 'status',
          header: 'Status',
          cell: (item) => (
            <span data-testid="locale-status-badge">
              <TranslationStatusIndicator status={item.status} />
            </span>
          ),
        },
        {
          id: 'translatedText',
          header: 'Translation',
          cell: (item) => (
            <span data-testid="locale-translation">{item.translatedText}</span>
          ),
        },
        {
          id: 'lastUpdatedOn',
          header: 'Last updated',
          cell: (item) => (
            <span data-testid="locale-updated-date">
              {DEMO_LAST_UPDATED[item.locale]?.lastUpdatedOn ?? '—'}
            </span>
          ),
        },
        {
          id: 'lastUpdatedBy',
          header: 'Last updated by',
          cell: (item) => (
            <span data-testid="locale-updated-by">
              {DEMO_LAST_UPDATED[item.locale]?.lastUpdatedBy ?? '—'}
            </span>
          ),
        },
      ]}
      empty={
        <Box textAlign="center" color="text-status-inactive" padding="l">
          No translations available for this string.
        </Box>
      }
    />
  );
};

export default LocalesTable;
